/**
 * My Marketing Dashboard.
 *
 * Personal command-center view for a marketing user. Answers
 * "How are my campaigns performing, what needs attention, what do
 *  I do next?" in one screen.
 *
 * Two tabs:
 *   - Overview          KPIs, alerts, campaigns, source/lead snapshot,
 *                       tasks, AI recs. Backed by /overview.
 *   - Source Analytics  Source / campaign / ad breakdown with source_id,
 *                       campaign_id, ad_id columns + AI-style answers
 *                       (top source, top qualified source, top booking
 *                       campaign, top conv ad, duplicates, stopped
 *                       sources, failed integrations). Backed by
 *                       /source-analytics.
 *
 * Each tab loads its own endpoint on first activation so cold load
 * stays cheap — we don't fetch the analytics payload until the user
 * actually clicks into the tab.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogBody, DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Sparkles, Users, Target, DollarSign, TrendingUp, TrendingDown,
  AlertCircle, Wallet, Tag, RefreshCw, ArrowRight, Activity,
  Megaphone, CheckSquare, Lightbulb, Clock, Flame, Award, Layers,
  Copy, ZapOff, XCircle, BarChart3,
  Plus, Phone, Mail, CalendarCheck, MessageSquare,
  Calendar, ClipboardList,
} from 'lucide-react';
import InfoBadge from '@/components/InfoBadge';
import {
  ManagementFeedback,
  AIAssistantPanel,
  MyDailyReport,
} from '@/components/ManagementColumn';

const API = process.env.REACT_APP_BACKEND_URL;

// Format an AED value with the locale-friendly comma separators
// + currency prefix. Used in nearly every section so kept tidy here.
const fmtAED = (n) => {
  if (n == null || Number.isNaN(Number(n))) return 'AED 0';
  return `AED ${Number(n).toLocaleString('en-AE', { maximumFractionDigits: 0 })}`;
};

// Render a percent delta with arrow + colour. Used on the KPI tiles
// so the user can see momentum at a glance.
const Delta = ({ pct }) => {
  if (pct == null) return <span className="text-xs text-slate-400">no prior</span>;
  const up = pct > 0;
  const flat = pct === 0;
  return (
    <span className={`text-xs inline-flex items-center gap-0.5 ${
      flat ? 'text-slate-500' : up ? 'text-emerald-600' : 'text-red-500'
    }`}>
      {flat ? '—' : up ? <TrendingUp size={11}/> : <TrendingDown size={11}/>}
      {flat ? 'flat' : `${up ? '+' : ''}${pct}%`}
    </span>
  );
};

const KpiTile = ({ label, value, sub, icon: Icon, tone = 'slate' }) => {
  const toneClasses = {
    slate:   'bg-slate-50 border-slate-200',
    violet:  'bg-violet-50 border-violet-200',
    emerald: 'bg-emerald-50 border-emerald-200',
    amber:   'bg-amber-50 border-amber-200',
    rose:    'bg-rose-50 border-rose-200',
    blue:    'bg-blue-50 border-blue-200',
  };
  const iconBgClasses = {
    slate:   'bg-slate-100 text-slate-600',
    violet:  'bg-violet-100 text-violet-600',
    emerald: 'bg-emerald-100 text-emerald-600',
    amber:   'bg-amber-100 text-amber-600',
    rose:    'bg-rose-100 text-rose-600',
    blue:    'bg-blue-100 text-blue-600',
  };
  return (
    <div className={`rounded-xl border p-4 ${toneClasses[tone]}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">{label}</p>
          <p className="text-2xl font-semibold text-slate-900 mt-1">{value}</p>
          {sub && <div className="mt-1">{sub}</div>}
        </div>
        {Icon && (
          <div className={`p-2 rounded-lg ${iconBgClasses[tone]}`}>
            <Icon size={16} />
          </div>
        )}
      </div>
    </div>
  );
};

const AlertRow = ({ alert }) => {
  const high = alert.severity === 'high';
  const iconMap = { alert: AlertCircle, wallet: Wallet, tag: Tag };
  const Icon = iconMap[alert.icon] || AlertCircle;
  return (
    <div className={`flex items-start gap-3 p-3 rounded-lg border ${
      high ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'
    }`}>
      <Icon size={16} className={high ? 'text-red-500 mt-0.5' : 'text-amber-500 mt-0.5'} />
      <div className="text-sm text-slate-700 flex-1">{alert.title}</div>
      <Badge variant="outline" className={`text-[10px] ${
        high ? 'border-red-300 text-red-700' : 'border-amber-300 text-amber-700'
      }`}>
        {alert.severity}
      </Badge>
    </div>
  );
};

const stageStyle = (stage) => {
  const s = (stage || '').toLowerCase();
  if (s.includes('closed')) return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  if (s.includes('booking')) return 'bg-blue-50 text-blue-700 border-blue-200';
  if (s.includes('negotiation')) return 'bg-violet-50 text-violet-700 border-violet-200';
  if (s.includes('site_visit')) return 'bg-amber-50 text-amber-700 border-amber-200';
  if (s.includes('qualified')) return 'bg-cyan-50 text-cyan-700 border-cyan-200';
  return 'bg-slate-50 text-slate-700 border-slate-200';
};

// ---------------------------------------------------------------------------
// Source Analytics tab — its own component so it can fetch lazily on first
// activation. Keeps the Overview tab loading time unchanged.
// ---------------------------------------------------------------------------
const InsightCard = ({ title, icon: Icon, accent, value, sub, empty, info }) => (
  // `info` renders a small (i) glyph on the right side of the title.
  // We use the native ``title`` attribute for the tooltip rather than
  // a popover — these cards are small and a hover hint is enough.
  // ARIA: aria-label makes the icon discoverable for keyboard /
  // screen-reader users; tabIndex makes it focusable so they can
  // surface the same tooltip on focus.
  <div className={`rounded-xl border p-4 ${accent}`}>
    <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-slate-500 font-medium">
      {Icon && <Icon size={12} />}
      <span className="flex-1">{title}</span>
      <InfoBadge text={info} />
    </div>
    {value ? (
      <>
        <p className="mt-1 text-base font-semibold text-slate-900">{value}</p>
        {sub && <p className="text-xs text-slate-600 mt-0.5">{sub}</p>}
      </>
    ) : (
      <p className="mt-1 text-sm text-slate-500">{empty || 'No data yet.'}</p>
    )}
  </div>
);

// The `filter` prop lets the caller (Performance tab dropdown) collapse
// the view to a single sub-section. Values: 'all' | 'ads' | 'leads' |
// 'sources' | 'campaigns'. Anything unknown falls through to 'all' so
// the component stays safe if a future filter code is passed in.
export function SourceAnalyticsTab({ token, filter = 'all' }) {
  const showAll        = filter === 'all';
  const showAiInsights = showAll;
  const showSources    = showAll || filter === 'sources';
  const showCampaigns  = showAll || filter === 'campaigns';
  const showAds        = showAll || filter === 'ads';
  const showLeads      = showAll || filter === 'leads';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/marketing/my/source-analytics`, {
        params: { token },
      });
      setData(r.data?.data || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load source analytics');
    } finally {
      setLoading(false);
    }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  if (loading && !data) {
    return (
      <div className="space-y-4 mt-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const sources   = data?.source_performance   || [];
  const campaigns = data?.campaign_performance || [];
  const ads       = data?.ad_performance       || [];
  const ai        = data?.ai_insights          || {};

  return (
    <div className="space-y-5 mt-4">
      {/* AI Insights — short, clickable cards at the top so the agent
          sees the punchline before scanning the tables. */}
      {showAiInsights && (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Lightbulb size={16} className="text-violet-500" />
            AI Insights
            <InfoBadge text="Pre-computed answers to the questions a marketing agent would otherwise have to eyeball from the tables below. Refreshed each time the dashboard is opened; window is the last 30 days unless otherwise noted." />
          </CardTitle>
          <CardDescription>
            Auto-answers for "which source / campaign / ad is winning right now."
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <InsightCard
              title="Top source (volume)"
              icon={Award}
              accent="bg-violet-50 border-violet-200"
              value={ai.top_source ? `${ai.top_source.source} · ${ai.top_source.source_id}` : null}
              sub={ai.top_source ? `${ai.top_source.leads} leads · ${ai.top_source.conv_pct}% conv` : null}
              info="Lead source that brought in the most total leads in the last 30 days. Ranked purely by volume — quality is shown in the next card."
            />
            <InsightCard
              title="Top source (qualified)"
              icon={Target}
              accent="bg-emerald-50 border-emerald-200"
              value={ai.top_qualified_source ? `${ai.top_qualified_source.source} · ${ai.top_qualified_source.source_id}` : null}
              sub={ai.top_qualified_source ? `${ai.top_qualified_source.qualified} qualified of ${ai.top_qualified_source.leads}` : null}
              info="Source whose leads progressed furthest down the funnel (qualified / site visit / negotiation / booking / closed). The quality counterpart to Top source (volume)."
            />
            <InsightCard
              title="Top campaign (bookings)"
              icon={Megaphone}
              accent="bg-amber-50 border-amber-200"
              value={ai.top_booking_campaign ? `${ai.top_booking_campaign.campaign}` : null}
              sub={ai.top_booking_campaign ? `${ai.top_booking_campaign.campaign_id} · ${ai.top_booking_campaign.won} won` : null}
              info="Campaign that produced the most closed-won bookings in the last 30 days. The ROI signal for ad spend — high won-count = budget well spent."
            />
            <InsightCard
              title="Top ad (conv rate)"
              icon={Flame}
              accent="bg-rose-50 border-rose-200"
              value={ai.top_conv_ad ? `${ai.top_conv_ad.ad_name}` : null}
              sub={ai.top_conv_ad ? `${ai.top_conv_ad.ad_id} · ${ai.top_conv_ad.conv_pct}%` : null}
              info="Individual ad with the best lead-to-won conversion rate (last 30 days). Use this creative as a template for the next campaign cycle."
            />
          </div>

          {/* Secondary insights — duplicates / stopped / failures.
              Each row carries its own info-icon explaining what the
              tile signals and what action to take. */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
            <div className="rounded-xl border border-orange-200 bg-orange-50 p-4">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-orange-600 font-medium">
                <Copy size={12} /> <span className="flex-1">Duplicate sources</span>
                <InfoBadge text="Lead sources where the same client phone number arrived multiple times in the last 30 days. High counts usually mean a webhook is re-firing or two ad accounts are pointing at the same form — check the source config." />
              </div>
              {ai.duplicate_sources?.length ? (
                <ul className="mt-1 space-y-0.5 text-sm text-slate-800">
                  {ai.duplicate_sources.slice(0, 4).map((d, i) => (
                    <li key={i}>
                      <span className="font-medium">{d.source}</span>{' '}
                      <span className="text-xs text-slate-500">({d.source_id})</span>{' '}
                      <span className="text-xs text-orange-700">· {d.duplicate_count} dup phones</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-1 text-sm text-slate-500">No duplicates detected.</p>
              )}
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-slate-600 font-medium">
                <ZapOff size={12} /> <span className="flex-1">Sources gone silent</span>
                <InfoBadge text="Sources that brought leads previously but produced nothing in the last 7 days. Likely an ad paused, a tracking URL changed, or a feed broke — worth a quick check before media spend rolls forward." />
              </div>
              {ai.stopped_sources?.length ? (
                <ul className="mt-1 space-y-0.5 text-sm text-slate-800">
                  {ai.stopped_sources.slice(0, 6).map((s, i) => (
                    <li key={i} className="capitalize">{(s || '').replace(/_/g, ' ')}</li>
                  ))}
                </ul>
              ) : (
                <p className="mt-1 text-sm text-slate-500">All sources still active in the last 7 days.</p>
              )}
            </div>
            <div className="rounded-xl border border-red-200 bg-red-50 p-4">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-red-600 font-medium">
                <XCircle size={12} /> <span className="flex-1">Failed integrations (24h)</span>
                <InfoBadge text="Webhook / API failures recorded in the last 24 hours from any connected source (Meta, Google, HubSpot, etc.). Errors here mean leads may be silently dropping — fix the integration before reading other counts." />
              </div>
              {ai.failed_integrations?.length ? (
                <ul className="mt-1 space-y-0.5 text-sm text-slate-800">
                  {ai.failed_integrations.slice(0, 4).map((f, i) => (
                    <li key={i}>
                      <span className="font-medium">{f.integration || f.source || 'integration'}</span>
                      {f.error && <span className="text-xs text-slate-500"> · {String(f.error).slice(0, 60)}</span>}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-1 text-sm text-slate-500">No integration failures in the last 24h.</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Source Performance — includes source_id */}
      {showSources && (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Activity size={16} className="text-blue-500" />
            Source Performance
          </CardTitle>
          <CardDescription>
            Leads, qualified, and conversion by lead source — last 30 days.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {sources.length === 0 ? (
            <p className="text-sm text-slate-500">No source data yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[11px] uppercase text-slate-500 border-b">
                  <tr>
                    <th className="text-left py-2 pr-3">Source</th>
                    <th className="text-left py-2 pr-3">Source ID</th>
                    <th className="text-right py-2 pr-3">Leads</th>
                    <th className="text-right py-2 pr-3">Qualified</th>
                    <th className="text-right py-2 pr-3">Won</th>
                    <th className="text-right py-2 pr-3">Lost</th>
                    <th className="text-right py-2 pr-3">Conv %</th>
                  </tr>
                </thead>
                <tbody>
                  {sources.map((s, i) => (
                    <tr key={`${s.source}-${s.source_id}-${i}`} className="border-b last:border-0">
                      <td className="py-2 pr-3 capitalize text-slate-800">
                        {(s.source || 'direct').replace(/_/g, ' ')}
                      </td>
                      <td className="py-2 pr-3 text-slate-500 font-mono text-xs">{s.source_id}</td>
                      <td className="py-2 pr-3 text-right">{s.leads}</td>
                      <td className="py-2 pr-3 text-right">{s.qualified}</td>
                      <td className="py-2 pr-3 text-right text-emerald-600">{s.won}</td>
                      {/* Lost rendered red-tinted to mirror Won's
                          green; zero stays slate so empty rows
                          don't draw too much attention. */}
                      <td className={`py-2 pr-3 text-right ${(s.lost || 0) > 0 ? 'text-red-500' : 'text-slate-400'}`}>
                        {s.lost ?? 0}
                      </td>
                      <td className="py-2 pr-3 text-right">
                        <span className={`font-medium ${
                          s.conv_pct >= 15 ? 'text-emerald-600' :
                          s.conv_pct >= 5  ? 'text-amber-600' :
                                              'text-red-500'
                        }`}>
                          {s.conv_pct}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* Campaign Performance — includes campaign_id and CPL */}
      {showCampaigns && (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Megaphone size={16} className="text-violet-500" />
            Campaign Performance
          </CardTitle>
          <CardDescription>
            CPL and conversion by campaign — last 30 days.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {campaigns.length === 0 ? (
            <p className="text-sm text-slate-500">
              No campaign attribution found. Run UTM-tagged campaigns or wire integrations to see this section populate.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[11px] uppercase text-slate-500 border-b">
                  <tr>
                    <th className="text-left py-2 pr-3">Campaign</th>
                    <th className="text-left py-2 pr-3">Campaign ID</th>
                    <th className="text-right py-2 pr-3">Leads</th>
                    <th className="text-right py-2 pr-3">Qualified</th>
                    <th className="text-right py-2 pr-3">Won</th>
                    <th className="text-right py-2 pr-3">Lost</th>
                    <th className="text-right py-2 pr-3">Cost</th>
                    <th className="text-right py-2 pr-3">CPL</th>
                    <th className="text-right py-2 pr-3">Conv %</th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map((c, i) => (
                    <tr key={`${c.campaign_id}-${i}`} className="border-b last:border-0">
                      <td className="py-2 pr-3 text-slate-800">{c.campaign}</td>
                      <td className="py-2 pr-3 text-slate-500 font-mono text-xs">{c.campaign_id}</td>
                      <td className="py-2 pr-3 text-right">{c.leads}</td>
                      <td className="py-2 pr-3 text-right">{c.qualified}</td>
                      <td className="py-2 pr-3 text-right text-emerald-600">{c.won}</td>
                      <td className={`py-2 pr-3 text-right ${(c.lost || 0) > 0 ? 'text-red-500' : 'text-slate-400'}`}>
                        {c.lost ?? 0}
                      </td>
                      <td className="py-2 pr-3 text-right">{fmtAED(c.cost)}</td>
                      <td className="py-2 pr-3 text-right">{c.cost ? fmtAED(c.cpl) : '—'}</td>
                      <td className="py-2 pr-3 text-right">
                        <span className={`font-medium ${
                          c.conv_pct >= 15 ? 'text-emerald-600' :
                          c.conv_pct >= 5  ? 'text-amber-600' :
                                              'text-red-500'
                        }`}>
                          {c.conv_pct}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* Ad Performance — includes ad_id */}
      {showAds && (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Layers size={16} className="text-rose-500" />
            Ad Performance
          </CardTitle>
          <CardDescription>
            Per-ad lead counts and quality — last 30 days.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {ads.length === 0 ? (
            <p className="text-sm text-slate-500">
              No ad-level attribution yet. Connect Meta / Google integrations or pass ad_id on incoming leads.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[11px] uppercase text-slate-500 border-b">
                  <tr>
                    <th className="text-left py-2 pr-3">Ad name</th>
                    <th className="text-left py-2 pr-3">Ad ID</th>
                    <th className="text-right py-2 pr-3">Leads</th>
                    <th className="text-right py-2 pr-3">Qualified</th>
                    <th className="text-right py-2 pr-3">Won</th>
                    <th className="text-right py-2 pr-3">Lost</th>
                    <th className="text-right py-2 pr-3">Conv %</th>
                  </tr>
                </thead>
                <tbody>
                  {ads.map((a, i) => (
                    <tr key={`${a.ad_id}-${i}`} className="border-b last:border-0">
                      <td className="py-2 pr-3 text-slate-800">{a.ad_name}</td>
                      <td className="py-2 pr-3 text-slate-500 font-mono text-xs">{a.ad_id}</td>
                      <td className="py-2 pr-3 text-right">{a.leads}</td>
                      <td className="py-2 pr-3 text-right">{a.qualified}</td>
                      <td className="py-2 pr-3 text-right text-emerald-600">{a.won}</td>
                      <td className={`py-2 pr-3 text-right ${(a.lost || 0) > 0 ? 'text-red-500' : 'text-slate-400'}`}>
                        {a.lost ?? 0}
                      </td>
                      <td className="py-2 pr-3 text-right">
                        <span className={`font-medium ${
                          a.conv_pct >= 15 ? 'text-emerald-600' :
                          a.conv_pct >= 5  ? 'text-amber-600' :
                                              'text-red-500'
                        }`}>
                          {a.conv_pct}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* Lead Source Performance — bottom-of-page summary that mirrors
          the widget on My Dashboard's Overview. Same `sources` data as
          the Source Performance table above; the compact layout (no
          Source ID / Cost columns) is meant as a quick "where are leads
          coming from" recap after the deep-dive tables. */}
      {showLeads && (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Activity size={16} className="text-blue-500" />
            Lead Source Performance
            <InfoBadge text="Per-source lead counts and conversion — same window as the tables above, presented as a compact summary. Conv % is colour-coded: green ≥15%, amber 5-15%, red <5%." />
          </CardTitle>
          <CardDescription>Where your leads are coming from + their conversion rate.</CardDescription>
        </CardHeader>
        <CardContent>
          {sources.length === 0 ? (
            <p className="text-sm text-slate-500">No leads yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-[11px] uppercase text-slate-500 border-b">
                <tr>
                  <th className="text-left py-2 pr-3">Source</th>
                  <th className="text-right py-2 pr-3">Leads</th>
                  <th className="text-right py-2 pr-3">Qualified</th>
                  <th className="text-right py-2 pr-3">Won</th>
                  <th className="text-right py-2 pr-3">Lost</th>
                  <th className="text-right py-2 pr-3">Conv %</th>
                </tr>
              </thead>
              <tbody>
                {sources.map((s, i) => (
                  <tr key={`lsp-${s.source}-${s.source_id}-${i}`} className="border-b last:border-0">
                    <td className="py-2 pr-3 capitalize text-slate-800">
                      {(s.source || 'direct').replace(/_/g, ' ')}
                    </td>
                    <td className="py-2 pr-3 text-right">{s.leads}</td>
                    <td className="py-2 pr-3 text-right">{s.qualified}</td>
                    <td className="py-2 pr-3 text-right text-emerald-600">{s.won ?? 0}</td>
                    <td className={`py-2 pr-3 text-right ${(s.lost || 0) > 0 ? 'text-red-500' : 'text-slate-400'}`}>
                      {s.lost ?? 0}
                    </td>
                    <td className="py-2 pr-3 text-right">
                      <span className={`font-medium ${
                        s.conv_pct >= 15 ? 'text-emerald-600' :
                        s.conv_pct >= 5  ? 'text-amber-600' :
                                            'text-red-500'
                      }`}>
                        {s.conv_pct}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
      )}

      <p className="text-xs text-slate-400 text-center">
        Window: last {data?.window_days ?? 30} days · As of {new Date(data?.as_of || Date.now()).toLocaleString('en-AE', { timeZone: 'Asia/Dubai' })}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CampaignAlertsAndActive — the two "campaign hygiene" cards from the
// Overview tab, exported so the main Marketing Dashboard can reuse them
// inside its Campaigns tab. Fetches its own data from
// /api/marketing/my/overview (same source as the Overview tab), so the
// widgets don't require the caller to already have this payload.
// ---------------------------------------------------------------------------
export function CampaignAlertsAndActive({ token }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/marketing/my/overview`, {
        params: { token },
      });
      setData(r.data?.data || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load campaign details');
    } finally {
      setLoading(false);
    }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  if (loading && !data) {
    return (
      <div className="space-y-4 mt-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  const alerts    = data?.alerts           || [];
  const campaigns = data?.active_campaigns || [];

  return (
    <div className="space-y-4">
      {/* Campaign Alerts — hidden entirely when there's nothing to
          flag; mirrors the Overview tab's behaviour so an all-clear
          view doesn't waste vertical space. */}
      {alerts.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <AlertCircle size={16} className="text-red-500" />
              Campaign Alerts
              <Badge variant="outline" className="ml-2">{alerts.length}</Badge>
              <InfoBadge text="Auto-generated warnings about your owned campaigns: high spend with no leads, budget nearing 100%, sources gone silent, leads with missing UTM tracking. Severity (high / medium / low) drives the row colour." />
            </CardTitle>
            <CardDescription>Issues that need your attention today.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {alerts.map((a, i) => <AlertRow key={i} alert={a} />)}
          </CardContent>
        </Card>
      )}

      {/* My Active Campaigns — same table + budget colour scheme as
          the Overview tab. Rows click through to the campaign detail. */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Megaphone size={16} className="text-violet-500" />
            My Active Campaigns
            <InfoBadge text="The 5 campaigns you currently own where status is active / running / live. Click any row to drill into the campaign's detail page. The Budget % chip flips amber at 70% and red at 90% so you can spot near-capacity spend at a glance." />
          </CardTitle>
          <CardDescription>Top 5 campaigns you own and are currently running.</CardDescription>
        </CardHeader>
        <CardContent>
          {campaigns.length === 0 ? (
            <p className="text-sm text-slate-500">No active campaigns. Launch one from Marketing → Campaigns.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[11px] uppercase text-slate-500 border-b">
                  <tr>
                    <th className="text-left py-2 pr-3">Campaign</th>
                    <th className="text-left py-2 pr-3">Platform</th>
                    <th className="text-right py-2 pr-3">Leads</th>
                    <th className="text-right py-2 pr-3">Spend</th>
                    <th className="text-right py-2 pr-3">CPL</th>
                    <th className="text-right py-2 pr-3">Budget %</th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map((c) => (
                    <tr key={c.id} className="border-b last:border-0 hover:bg-slate-50 cursor-pointer"
                        onClick={() => navigate(`/marketing/campaigns?campaign=${c.id}`)}>
                      <td className="py-2 pr-3 font-medium text-slate-800">{c.name}</td>
                      <td className="py-2 pr-3 text-slate-600 capitalize">{c.platform || '-'}</td>
                      <td className="py-2 pr-3 text-right">{c.leads}</td>
                      <td className="py-2 pr-3 text-right">{fmtAED(c.spend)}</td>
                      <td className="py-2 pr-3 text-right">{fmtAED(c.cpl)}</td>
                      <td className="py-2 pr-3 text-right">
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          c.budget_pct >= 90 ? 'bg-red-100 text-red-700' :
                          c.budget_pct >= 70 ? 'bg-amber-100 text-amber-700' :
                                                'bg-emerald-100 text-emerald-700'
                        }`}>
                          {c.budget_pct}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Overview tab — wraps the original single-page content so adding tabs
// doesn't visually change what the user already had.
// ---------------------------------------------------------------------------
function OverviewTab({ token, navigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/marketing/my/overview`, {
        params: { token },
      });
      setData(r.data?.data || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load dashboard');
    } finally {
      setLoading(false);
    }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  if (loading && !data) {
    return (
      <div className="space-y-4 mt-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const k = data?.kpis || {};
  const alerts = data?.alerts || [];
  const campaigns = data?.active_campaigns || [];
  const sources = data?.source_perf || [];
  const recent = data?.recent_leads || [];
  const tasks = data?.tasks || [];
  const recs = data?.recommendations || [];

  return (
    <div className="space-y-5 mt-4">
      {/* Section 1 — KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <KpiTile
          label="Leads (Today)"
          value={k.leads_today ?? 0}
          sub={<span className="text-xs text-slate-500">{k.leads_month ?? 0} MTD</span>}
          icon={Users}
          tone="violet"
        />
        <KpiTile
          label="Leads (MTD)"
          value={k.leads_month ?? 0}
          sub={<Delta pct={k.leads_month_delta_pct} />}
          icon={Users}
          tone="violet"
        />
        <KpiTile
          label="Qualified (MTD)"
          value={k.qualified_month ?? 0}
          sub={<span className="text-xs text-slate-500">
            {k.leads_month ? `${Math.round(100 * k.qualified_month / k.leads_month)}% of leads` : '—'}
          </span>}
          icon={Target}
          tone="emerald"
        />
        <KpiTile
          label="CPL"
          value={fmtAED(k.cpl)}
          sub={<span className="text-xs text-slate-500">cost ÷ leads</span>}
          icon={DollarSign}
          tone="amber"
        />
        <KpiTile
          label="Budget Used"
          value={`${k.budget_util_pct ?? 0}%`}
          sub={<span className="text-xs text-slate-500">{k.campaigns_managed ?? 0} campaigns</span>}
          icon={Wallet}
          tone="rose"
        />
        <KpiTile
          label="Conv. rate"
          value={`${k.conversion_rate_pct ?? 0}%`}
          sub={<span className="text-xs text-slate-500">leads → won</span>}
          icon={TrendingUp}
          tone="emerald"
        />
        <KpiTile
          label="Revenue Influenced"
          value={fmtAED(k.revenue_influenced)}
          sub={<span className="text-xs text-slate-500">MTD won deals</span>}
          icon={Flame}
          tone="blue"
        />
      </div>

      {/* Section 2 — Alerts */}
      {alerts.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <AlertCircle size={16} className="text-red-500" />
              Campaign Alerts
              <Badge variant="outline" className="ml-2">{alerts.length}</Badge>
              <InfoBadge text="Auto-generated warnings about your owned campaigns: high spend with no leads, budget nearing 100%, sources gone silent, leads with missing UTM tracking. Severity (high / medium / low) drives the row colour." />
            </CardTitle>
            <CardDescription>Issues that need your attention today.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {alerts.map((a, i) => <AlertRow key={i} alert={a} />)}
          </CardContent>
        </Card>
      )}

      {/* Section 3 — My Active Campaigns */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Megaphone size={16} className="text-violet-500" />
            My Active Campaigns
            <InfoBadge text="The 5 campaigns you currently own where status is active / running / live. Click any row to drill into the campaign's detail page. The Budget % chip flips amber at 70% and red at 90% so you can spot near-capacity spend at a glance." />
          </CardTitle>
          <CardDescription>Top 5 campaigns you own and are currently running.</CardDescription>
        </CardHeader>
        <CardContent>
          {campaigns.length === 0 ? (
            <p className="text-sm text-slate-500">No active campaigns. Launch one from Marketing → Campaigns.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[11px] uppercase text-slate-500 border-b">
                  <tr>
                    <th className="text-left py-2 pr-3">Campaign</th>
                    <th className="text-left py-2 pr-3">Platform</th>
                    <th className="text-right py-2 pr-3">Leads</th>
                    <th className="text-right py-2 pr-3">Spend</th>
                    <th className="text-right py-2 pr-3">CPL</th>
                    <th className="text-right py-2 pr-3">Budget %</th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map((c) => (
                    <tr key={c.id} className="border-b last:border-0 hover:bg-slate-50 cursor-pointer"
                        onClick={() => navigate(`/marketing/campaigns?campaign=${c.id}`)}>
                      <td className="py-2 pr-3 font-medium text-slate-800">{c.name}</td>
                      <td className="py-2 pr-3 text-slate-600 capitalize">{c.platform || '-'}</td>
                      <td className="py-2 pr-3 text-right">{c.leads}</td>
                      <td className="py-2 pr-3 text-right">{fmtAED(c.spend)}</td>
                      <td className="py-2 pr-3 text-right">{fmtAED(c.cpl)}</td>
                      <td className="py-2 pr-3 text-right">
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          c.budget_pct >= 90 ? 'bg-red-100 text-red-700' :
                          c.budget_pct >= 70 ? 'bg-amber-100 text-amber-700' :
                                                'bg-emerald-100 text-emerald-700'
                        }`}>
                          {c.budget_pct}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>


      {/* Two-column row: Tasks + AI Recommendations */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Section 6 — My Tasks */}
        <MyTasksCard tasks={tasks} token={token} onChanged={load} />
        {/* (The card was inlined here originally; promoted to its own
            component so the Add Task modal + reload logic stays
            scoped and the surrounding dashboard JSX stays compact.) */}

        {/* Section 7 — AI Recommendations */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Lightbulb size={16} className="text-violet-500" />
              AI Recommendations
              <InfoBadge text="Next-best-action suggestions generated by the AI assistant by analysing your funnel data. Confidence badge (High / Medium / Low) reflects how strong the supporting signal is. Refreshed daily." />
            </CardTitle>
            <CardDescription>What the assistant suggests you do next.</CardDescription>
          </CardHeader>
          <CardContent>
            {recs.length === 0 ? (
              <p className="text-sm text-slate-500">No recommendations yet. The assistant refreshes daily.</p>
            ) : (
              <div className="space-y-3">
                {recs.map((r) => (
                  <div key={r.id} className="p-3 rounded-lg border border-violet-200 bg-violet-50/40">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-semibold text-slate-900">{r.title}</p>
                      <Badge variant="outline" className={`text-[10px] ${
                        r.confidence === 'High' ? 'border-emerald-300 text-emerald-700' :
                        r.confidence === 'Low'  ? 'border-slate-300 text-slate-600' :
                                                  'border-amber-300 text-amber-700'
                      }`}>
                        {r.confidence}
                      </Badge>
                    </div>
                    <p className="text-xs text-slate-600 mt-1">{r.body}</p>
                    {r.action && r.action_link && (
                      <button
                        onClick={() => navigate(r.action_link)}
                        className="mt-2 text-xs text-violet-700 hover:text-violet-900 font-medium flex items-center gap-1"
                      >
                        {r.action} <ArrowRight size={11} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Section 8 — Management Feedback + AI Assistant + Daily Report.
          Mirrors the "Right Column" on the sales agent's My-Dashboard
          so a marketing user gets the same three personal-productivity
          surfaces: read CEO/HOD messages, chat with the AI assistant
          to log activity, and review their auto-generated daily
          report. Two-column layout — feedback takes the wider lane
          because messages can be long; the chat + report stack on the
          right at equal height. */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <ManagementFeedback token={token} />
        </div>
        <div className="space-y-4">
          <AIAssistantPanel token={token} />
          <MyDailyReport token={token} />
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page shell with tabbed layout.
// ---------------------------------------------------------------------------
export default function MyMarketingDashboard() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  // Bump this counter to force the Overview data hook to reload — the
  // child component is keyed off it so a refresh remounts it.
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = () => setRefreshKey((k) => k + 1);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-[1600px] mx-auto p-6 space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Sparkles size={24} className="text-violet-500" />
              My Dashboard
              <InfoBadge text="Your personal command centre — scoped to the campaigns and leads you own (or, for managers, your team's full book). Refreshes on every page load; numbers are computed on the fly so they reflect the live database." />
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Welcome back, {user?.full_name || user?.email}. Your campaign performance, leads, alerts and next actions.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={refresh}>
            <RefreshCw size={14} className="mr-1.5" />
            Refresh
          </Button>
        </div>

        <OverviewTab key={`ov-${refreshKey}`} token={token} navigate={navigate} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// MyTasksCard — list + create-task modal.
//
// Backed by /api/tasks (full CRUD already exists in
// routes/tasks_routes.py). We expose the five task types the backend
// already supports — Task / Follow-up / Call / Email / Meeting — so
// "follow-ups" don't need a separate collection or endpoint.
// After a successful create we call onChanged() to re-pull the parent
// dashboard's data so the new row shows up in the list right away.
// ---------------------------------------------------------------------------

// Map task_type → icon + label so the row chip reads naturally. The
// keys match the values backend Task model accepts (tasks_routes.py:27).
const TASK_TYPE_META = {
  follow_up: { label: 'Follow-up', icon: MessageSquare, tone: 'bg-violet-100 text-violet-700 border-violet-200' },
  call:      { label: 'Call',      icon: Phone,         tone: 'bg-sky-100 text-sky-700 border-sky-200' },
  email:     { label: 'Email',     icon: Mail,          tone: 'bg-blue-100 text-blue-700 border-blue-200' },
  meeting:   { label: 'Meeting',   icon: CalendarCheck, tone: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  other:     { label: 'Task',      icon: CheckSquare,   tone: 'bg-slate-100 text-slate-700 border-slate-200' },
};

function MyTasksCard({ tasks, token, onChanged }) {
  // Same modal, two entry points. `mode` primes the task_type so
  // Add Task lands as `other` and Add Follow-up as `follow_up` —
  // matches the SalesReportingDashboard behaviour exactly.
  const [modalMode, setModalMode] = useState(null); // 'task' | 'follow_up' | null
  const open = modalMode !== null;
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <CheckSquare size={16} className="text-emerald-500" />
              My Tasks
            </CardTitle>
            <CardDescription>Pending work — sorted by due date.</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="h-8"
              onClick={() => setModalMode('task')}
              data-testid="mmd-add-task-btn"
            >
              <ClipboardList size={14} className="mr-1.5" />
              Add Task
            </Button>
            <Button
              size="sm"
              className="h-8 bg-violet-600 hover:bg-violet-700 text-white"
              onClick={() => setModalMode('follow_up')}
              data-testid="mmd-add-followup-btn"
            >
              <Plus size={14} className="mr-1.5" />
              Add Follow-up
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {tasks.length === 0 ? (
          <p className="text-sm text-slate-500">
            All clear — no pending tasks. Click <strong>Add</strong> to log a follow-up or schedule a call.
          </p>
        ) : (
          <div className="space-y-2">
            {tasks.map((t) => {
              const meta = TASK_TYPE_META[t.task_type || t.type] || TASK_TYPE_META.other;
              const Icon = meta.icon;
              return (
                <div key={t.id} className="flex items-center justify-between gap-2 p-2 rounded hover:bg-slate-50">
                  <span className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md border ${meta.tone}`}>
                    <Icon size={11} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-800 truncate">{t.title}</p>
                    <p className="text-xs text-slate-500 flex items-center gap-1.5">
                      <Badge variant="outline" className="text-[9px] py-0 px-1.5 font-normal">
                        {meta.label}
                      </Badge>
                      {t.due_date && (
                        <span className="flex items-center gap-1">
                          <Clock size={10} /> due {new Date(t.due_date).toLocaleDateString()}
                        </span>
                      )}
                    </p>
                  </div>
                  <Badge variant="outline" className={`text-[10px] ml-1 ${
                    t.priority === 'high' || t.priority === 'urgent' ? 'border-red-300 text-red-700' :
                    t.priority === 'medium' ? 'border-amber-300 text-amber-700' :
                                              'border-slate-300 text-slate-600'
                  }`}>
                    {t.priority || 'normal'}
                  </Badge>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
      <AddTaskModal
        open={open}
        mode={modalMode}
        onOpenChange={(o) => { if (!o) setModalMode(null); }}
        token={token}
        onCreated={() => { setModalMode(null); onChanged?.(); }}
      />
    </Card>
  );
}

function AddTaskModal({ open, mode, onOpenChange, token, onCreated }) {
  const isFollowUp = mode === 'follow_up';
  const [title, setTitle] = useState('');
  const [taskType, setTaskType] = useState(isFollowUp ? 'follow_up' : 'other');
  const [priority, setPriority] = useState('medium');
  const [dueDate, setDueDate] = useState('');
  const [dueTime, setDueTime] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  // Reset every time the modal closes — keeps the form blank for the
  // next open. When it opens fresh, prime task_type from the mode the
  // user clicked so Add Follow-up doesn't require an extra dropdown pick.
  useEffect(() => {
    if (!open) {
      setTitle('');
      setPriority('medium');
      setDueDate('');
      setDueTime('');
      setDescription('');
    } else {
      setTaskType(isFollowUp ? 'follow_up' : 'other');
    }
  }, [open, isFollowUp]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!title.trim()) {
      toast.error('Title is required');
      return;
    }
    setSaving(true);
    try {
      // Roll date + time into one ISO due_date on the wire. Matches
      // the SalesReportingDashboard flow so both dashboards produce
      // records the /api/agent/follow-ups splitter can bucket the
      // same way.
      let dueISO = null;
      if (dueDate) {
        const timePart = dueTime || '09:00';
        dueISO = new Date(`${dueDate}T${timePart}:00`).toISOString();
      }
      const payload = {
        title: title.trim(),
        description: description.trim(),
        task_type: taskType,
        priority,
        due_date: dueISO,
      };
      await axios.post(`${API}/api/tasks`, payload, { params: { token } });
      toast.success(`${TASK_TYPE_META[taskType]?.label || 'Task'} added`);
      onCreated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || 'Could not create task');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg p-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-4 border-b bg-gradient-to-r from-violet-50 to-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-violet-100 text-violet-600 flex items-center justify-center shrink-0">
              {isFollowUp ? <Calendar size={20} /> : <ClipboardList size={20} />}
            </div>
            <div className="flex-1">
              <DialogTitle className="text-lg font-semibold text-slate-900">
                {isFollowUp ? 'Add Follow-up' : 'Add Task'}
              </DialogTitle>
              <DialogDescription className="text-xs text-slate-500 mt-0.5">
                {isFollowUp
                  ? 'Schedule a reminder to circle back on a lead — sits on this card sorted by due date.'
                  : 'Log a piece of pending work — sits on this card sorted by due date.'}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <form onSubmit={submit}>
          <div className="px-6 py-5 space-y-5 max-h-[65vh] overflow-y-auto">
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                Title
              </Label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={isFollowUp
                  ? 'e.g. Circle back with Aisha about the JVC unit'
                  : 'e.g. Prepare Q3 pipeline report'}
                autoFocus
                className="h-10"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Type</Label>
                <Select value={taskType} onValueChange={setTaskType}>
                  <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(TASK_TYPE_META).map(([key, m]) => (
                      <SelectItem key={key} value={key}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Priority</Label>
                <Select value={priority} onValueChange={setPriority}>
                  <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                Description
              </Label>
              <Textarea
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What's the next step or context worth remembering?"
                className="resize-none"
              />
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-4">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-3">
                When
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-600 font-medium">Due date</Label>
                  <Input
                    type="date"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                    className="h-10 bg-white"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-600 font-medium">Due time</Label>
                  <Input
                    type="time"
                    value={dueTime}
                    onChange={(e) => setDueTime(e.target.value)}
                    className="h-10 bg-white"
                  />
                </div>
              </div>
              <p className="text-[11px] mt-2 text-slate-400">
                Leave blank for a no-due-date item — it'll sit at the end of the list.
              </p>
            </div>
          </div>
          <DialogFooter className="px-6 py-4 border-t bg-slate-50/70 gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving || !title.trim()}
              className="bg-violet-600 hover:bg-violet-700 text-white shadow-sm"
            >
              {saving ? (
                <><RefreshCw size={14} className="mr-2 animate-spin" /> Saving…</>
              ) : (
                <><Plus size={14} className="mr-2" /> {isFollowUp ? 'Add Follow-up' : 'Add Task'}</>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
